import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Config from '../utils/Config';
import InlineVerificationCode from './VerifyCodePanel';
import EmailRegisterForm from "./EmailRegisterForm";
import CreateAccount from "./CreateAccount";
import useTitle from '../hooks/useTitle';
import { displayMessage } from '../utils/functions';
import "./Login.css";
import "./Register.css";

const Register = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);
  const initialEmail = query.get("email") || "";

  const [email, setEmail] = useState(initialEmail);

  // MFA (mfaPending-based) — align with Login.js terminology/shape
  const [verificationPending, setVerificationPending] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [mfaChallengeId, setMfaChallengeId] = useState(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);

  // UI / messaging
  const [messageClass, setMessageClass] = useState("warning hidden");
  const [pendingMessage, setPendingMessage] = useState("");
  const [pendingStyle, setPendingStyle] = useState("warning");
  const [isLockedOut, setIsLockedOut] = useState(false);

  const emailInputRef = useRef(null);
  const messageRef = useRef(null);

  useTitle('Register');

  // Clean obsolete ?step=code from URL if present (won't use it anymore)
  useEffect(() => {
    const step = query.get("step");
    if (step) {
      const params = new URLSearchParams(location.search);
      params.delete("step");
      const qs = params.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus email on mount
  useEffect(() => { emailInputRef.current?.focus(); }, []);

  // Show pending message when waiting for code entry
  useEffect(() => {
    if (verificationPending && !codeVerified && pendingMessage && messageRef.current) {
      displayMessage(messageRef, pendingMessage, pendingStyle, setMessageClass);
    }
  }, [verificationPending, codeVerified, pendingMessage, pendingStyle]);

  // Soft focus trap until email is filled
  useEffect(() => {
    const handleFocusTrap = (e) => {
      if (email.trim()) return;
      e.preventDefault();
      e.stopPropagation();
      emailInputRef.current?.focus();
    };
    document.addEventListener("mousedown", handleFocusTrap, true);
    return () => document.removeEventListener("mousedown", handleFocusTrap, true);
  }, [email]);

  const validateForm = () => {
    if (!email.trim()) {
      displayMessage(messageRef, "❌ Email is required.", "warning", setMessageClass);
      emailInputRef.current?.focus();
      return false;
    }
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (messageRef.current) {
      messageRef.current.style.display = "none";
      setMessageClass("hidden");
    }

    try {
      const res = await fetch(
        `${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_REGISTER}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        displayMessage(
          messageRef,
          data.message || "❌ Registration failed. Try again.",
          "warning",
          setMessageClass
        );
        return;
      }

      // New flow: server tells us if verification (MFA) is pending and gives a challenge
      if (data.needsVerification) {
        setVerificationPending(true);
        if (data.challengeId) setMfaChallengeId(data.challengeId);
        if (data.codeExpiresAt) setCodeExpiresAt(data.codeExpiresAt);
        setPendingMessage("✅ Verification code sent. Please check email.");
        setPendingStyle("success");
      }

      if (data.message) {
        displayMessage(messageRef, data.message, "success", setMessageClass);
      }
    } catch {
      displayMessage(messageRef, "❌ Error connecting to the server. Please try again.", "warning", setMessageClass);
    }
  };

  const handleCodeSubmit = async (enteredCode) => {
    if (!mfaChallengeId) {
      displayMessage(messageRef, "❌ Challenge missing. Restart registration.", "warning", setMessageClass);
      return;
    }
    try {
      const res = await fetch(
        `${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_REGISTER_MFA_VERIFY}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ challengeId: mfaChallengeId, code: enteredCode }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (res.status === 429) {
        setIsLockedOut(true);
        displayMessage(
          messageRef,
          data.message || "❌ Too many attempts. Try again later.",
          "warning",
          setMessageClass
        );
        return;
      }

      if (res.ok && data.token) {
        // Store token for CreateAccount (parallels Login.js handoff)
        localStorage.setItem("regToken", data.token);
        setCodeVerified(true);
      } else {
        displayMessage(
          messageRef,
          data.message || "❌ Invalid or expired code.",
          "warning",
          setMessageClass
        );
      }
    } catch {
      displayMessage(messageRef, "❌ Server error. Try again later.", "warning", setMessageClass);
    }
  };

  const handleResend = async () => {
    if (!mfaChallengeId) {
      displayMessage(messageRef, "❌ Challenge missing. Restart registration.", "warning", setMessageClass);
      return;
    }
    try {
      const r = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_REGISTER_MFA_RESEND}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId: mfaChallengeId }),
      });
      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        if (r.status === 429) {
          const ra = r.headers.get('Retry-After');
          if (ra) displayMessage(messageRef, `Too many requests. Try again in ${ra}s.`, "warning", setMessageClass);
        } else {
          displayMessage(messageRef, data?.message || "Could not resend code.", "warning", setMessageClass);
        }
        return;
      }

      if (data.codeExpiresAt) setCodeExpiresAt(data.codeExpiresAt);
      displayMessage(messageRef, "✅ Code resent. Check your email.", "success", setMessageClass);
    } catch {
      displayMessage(messageRef, "❌ Failed to resend code.", "warning", setMessageClass);
    }
  };

  return (
    <div className="login-container login-container-register">
      <div className="login-box" id="register-container">
        <div ref={messageRef} className={messageClass} id="message" />
        {codeVerified ? (
          <CreateAccount email={email} />
        ) : verificationPending ? (
          <InlineVerificationCode
            onSubmit={handleCodeSubmit}
            onResend={handleResend}
            messageClass={messageClass}
            messageRef={messageRef}
            emailInputRef={emailInputRef}
            codeExpiresAt={codeExpiresAt}
            setExpiresAt={setCodeExpiresAt}
            setPendingMessage={setPendingMessage}
            setMessageClass={setMessageClass}
            setPendingStyle={setPendingStyle}
            email={email}
            isLockedOut={isLockedOut}
          />
        ) : (
          <EmailRegisterForm
            email={email}
            setEmail={setEmail}
            handleRegister={handleRegister}
            messageClass={messageClass}
            messageRef={messageRef}
            emailInputRef={emailInputRef}
          />
        )}
      </div>
    </div>
  );
};

export default Register;